import React, { useEffect, useState } from 'react';
import API from './api';

// Helpers
function formatDateLong(date) {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d)) return '-';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
function formatDateShort(date) {
  if (!date) return '-';
  if (typeof date === 'string' && date.length > 10) return date.slice(0, 10);
  return date;
}
function getAgeMonths(dob) {
  if (!dob) return '-';
  const birth = new Date(dob);
  if (isNaN(birth)) return '-';
  const now = new Date();
  let months = (now.getFullYear() - birth.getFullYear()) * 12 +
    (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) months--;
  return months;
}
function getParent(parents, rel) {
  if (!parents) return null;
  return parents.find(p => p.relation === rel) || null;
}

function CardRow({ label, value, preLine }) {
  return (
    <div className="flex gap-2 text-sm py-0.5">
      <span className="font-semibold min-w-[130px]">{label}:</span>
      <span className={`text-gray-700 ${preLine ? 'whitespace-pre-line' : ''}`}>
        {value ?? <span className="text-gray-400">-</span>}
      </span>
    </div>
  );
}
function CardRowVert({ label, value }) {
  const has = typeof value === 'string' ? value.trim() !== '' : !!value;
  return (
    <div className="mb-3">
      <div className="font-semibold text-sm mb-1">{label}:</div>
      <div className="text-gray-700 text-sm whitespace-pre-line">
        {has ? value : <span className="text-gray-400">-</span>}
      </div>
    </div>
  );
}

// ---- DISCIPLINE BADGES (iz student.disciplines) ----
function DisciplineBadges({ disciplines }) {
  if (!disciplines || disciplines.length === 0) return null;

  const styleByService = {
    EI: {
      label: 'EI',
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-300',
    },
    PI: {
      label: 'PI',
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-300',
    },
    ABA: {
      label: 'ABA',
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-300',
    },
    OT: {
      label: 'OT',
      bg: 'bg-teal-100',
      text: 'text-teal-800',
      border: 'border-teal-300',
    },
    PT: {
      label: 'PT',
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-300',
    },
  };

  // primarni prvi
  const sorted = [...disciplines].sort(
    (a, b) => Number(b.is_primary) - Number(a.is_primary)
  );

  return (
    <div className="flex flex-wrap gap-1 ml-1">
      {sorted.map(d => {
        const cfg = styleByService[d.service_type] || {
          label: d.service_type || '?',
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          border: 'border-gray-300',
        };
        const isPrimary = !!d.is_primary;

        const base = 'px-2 py-0.5 rounded-full text-[10px] font-semibold border';
        const colors = isPrimary
          ? `${cfg.bg} ${cfg.text} ${cfg.border}`
          : `bg-white ${cfg.text} ${cfg.border}`;

        return (
          <span
            key={d.id}
            className={`${base} ${colors}`}
            title={d.name || cfg.label}
          >
            {cfg.label}
          </span>
        );
      })}
    </div>
  );
}

function StudentCard({ student, expanded, onClick }) {
  const mother = getParent(student.parents, 'mother');
  const father = getParent(student.parents, 'father');

  const fullName = `${student.last_name || ''}${student.last_name ? ', ' : ''}${student.first_name || ''}`;

  return (
    <div
      className={`w-full px-2 sm:px-4 mx-auto bg-white rounded-xl shadow p-4 mb-6 transition-all border cursor-pointer
        ${expanded ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'}`}
      onClick={onClick}
    >
      {/* Osnovni podaci */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="text-lg font-bold">{fullName}</div>
          <div className="text-xs text-gray-600">
            UCI: {student.uci || <span className="text-gray-300">n/a</span>}
          </div>
          <div className="text-xs text-gray-500">
            DOB: {formatDateShort(student.dob)}
          </div>
          <div className="text-xs text-gray-500">
            {student.address_city || '-'}, {student.address_state || '-'}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold
                ${student.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}
            >
              {student.is_active ? 'Active' : 'Inactive'}
            </span>
            {/* discipline umesto program_type */}
            <DisciplineBadges disciplines={student.disciplines} />
          </div>
        </div>
      </div>

      {/* Detalji – prikaz samo ako je expanded */}
      {expanded && (
        <div className="mt-4 border-t pt-4 animate-fade-in space-y-6">
          {/* BASIC */}
          <div>
            <div className="font-semibold mb-2 text-blue-900 text-base">Basic Information</div>
            <CardRow label="First Name" value={student.first_name} />
            <CardRow label="Last Name" value={student.last_name} />
            <CardRow label="UCI" value={student.uci} />
            <CardRow label="Date of Birth" value={formatDateLong(student.dob)} />
            <CardRow
              label="Age"
              value={
                getAgeMonths(student.dob) !== '-'
                  ? getAgeMonths(student.dob) + ' months'
                  : '-'
              }
            />
            <CardRow label="Gender" value={student.gender} />
            <CardRow label="Start of Service" value={formatDateLong(student.service_start_date)} />
            <CardRow label="Onboarding Documents" value={student.onboarding_documents} />
          </div>

          {/* ADDRESS */}
          <div>
            <div className="font-semibold mb-2 text-blue-900 text-base">Address Information</div>
            <CardRow label="Street" value={student.address_street} />
            <CardRow label="City" value={student.address_city} />
            <CardRow label="State" value={student.address_state} />
            <CardRow label="ZIP" value={student.address_zip} />
            {student.notes && student.notes.trim() !== '' && (
              <CardRowVert label="Address Notes" value={student.notes} />
            )}
          </div>

          {/* PARENTS */}
          <div>
            <div className="font-semibold mb-2 text-blue-900 text-base">Parents/Guardians</div>
            <CardRow
              label="Mother"
              value={
                mother
                  ? [mother.first_name, mother.last_name].filter(Boolean).join(' ')
                  : '-'
              }
            />
            <CardRow label="Phone" value={mother?.phone || '-'} />
            <CardRow
              label="Father"
              value={
                father
                  ? [father.first_name, father.last_name].filter(Boolean).join(' ')
                  : '-'
              }
            />
            <CardRow label="Phone" value={father?.phone || '-'} />
          </div>

          {/* REFERRAL */}
          <div>
            <div className="font-semibold mb-2 text-blue-900 text-base">Referral Information</div>
            <CardRow label="Referring Agent" value={student.referring_agent} />
            <CardRow label="Unit" value={student.referring_agent_unit} />
            <CardRow label="Unit ID" value={student.referring_agent_unit_id} />
          </div>

          {/* SERVICE STATUS */}
          <div>
            <div className="font-semibold mb-2 text-blue-900 text-base">Service Status</div>
            <CardRow label="Status" value={student.is_active ? 'Active' : 'Inactive'} />
            <CardRow label="Start of Service Date" value={formatDateLong(student.service_start_date)} />
            <CardRow label="End of Service Date" value={formatDateLong(student.service_end_date)} />
          </div>

          {/* DEVELOPMENT GOALS */}
          <div>
            <div className="font-semibold mb-2 text-blue-900 text-base">Development Goals &amp; Notes</div>
            <CardRowVert label="Intake Development Goals" value={student.intake_goals} />
            <CardRowVert label="Additional Notes" value={student.additional_notes} />
            <CardRowVert label="Parent Concerns" value={student.parent_concerns} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyStudentsInfo() {
  const [students, setStudents] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'

  useEffect(() => {
    API.get('/my-students').then(res => setStudents(res.data || []));
  }, []);

  // filter po statusu
  const filteredStudents = students.filter(stu => {
    if (statusFilter === 'active') return !!stu.is_active;
    if (statusFilter === 'inactive') return !stu.is_active;
    return true; // all
  });

  // sortiranje po prezimenu, pa po imenu
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    const aLast = (a.last_name || '').toLowerCase();
    const bLast = (b.last_name || '').toLowerCase();
    if (aLast < bLast) return -1;
    if (aLast > bLast) return 1;

    const aFirst = (a.first_name || '').toLowerCase();
    const bFirst = (b.first_name || '').toLowerCase();
    if (aFirst < bFirst) return -1;
    if (aFirst > bFirst) return 1;

    return 0;
  });

  const btnBase =
    'px-3 py-1 text-xs font-semibold rounded-full border transition-colors';
  const activeCls = 'bg-blue-600 text-white border-blue-700 shadow-sm';
  const inactiveCls =
    'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';

  return (
    <div className="w-full px-2 sm:px-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold">My Students</h2>
        <div className="flex gap-2">
          <button
            type="button"
            className={`${btnBase} ${
              statusFilter === 'all' ? activeCls : inactiveCls
            }`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          <button
            type="button"
            className={`${btnBase} ${
              statusFilter === 'active' ? activeCls : inactiveCls
            }`}
            onClick={() => setStatusFilter('active')}
          >
            Active
          </button>
          <button
            type="button"
            className={`${btnBase} ${
              statusFilter === 'inactive' ? activeCls : inactiveCls
            }`}
            onClick={() => setStatusFilter('inactive')}
          >
            Inactive
          </button>
        </div>
      </div>

      {sortedStudents.length === 0 ? (
        <div className="text-gray-400 py-16 text-center">No students found.</div>
      ) : (
        sortedStudents.map(stu => (
          <StudentCard
            key={stu.id}
            student={stu}
            expanded={expandedId === stu.id}
            onClick={() =>
              setExpandedId(expandedId === stu.id ? null : stu.id)
            }
          />
        ))
      )}
    </div>
  );
}









